import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'

export const useTeamMembersStore = defineStore('teamMembers', () => {
  const uiStore = useUIStore()

  // State
  const teamMembers = ref([])
  const loading = ref(false)

  // API Functions
  const fetchTeamMembers = async () => {
    try {
      loading.value = true
      logService.info('Fetching team members...')

      const response = await apiService.get('/team-members')

      if (response.success) {
        teamMembers.value = response.data
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch team members')
      }
    } catch (error) {
      logService.error('Fetch Team Members Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: error.message
      })
      return []
    } finally {
      loading.value = false
    }
  }

  const createTeamMember = async (memberData) => {
    try {
      loading.value = true
      logService.info('Creating team member...', memberData)

      const response = await apiService.post('/team-members', memberData)

      if (response.success) {
        teamMembers.value.push(response.data)

        uiStore.showNotification({
          type: 'success',
          title: 'Team Member Created',
          message: 'Team member has been created successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to create team member')
      }
    } catch (error) {
      logService.error('Create Team Member Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Creation Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updateTeamMember = async (id, memberData) => {
    try {
      loading.value = true
      logService.info(`Updating team member: ${id}`, memberData)

      const response = await apiService.put(`/team-members/${id}`, memberData)

      if (response.success) {
        const index = teamMembers.value.findIndex(m => m.id === id)
        if (index !== -1) {
          teamMembers.value[index] = response.data
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Team Member Updated',
          message: 'Team member has been updated successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update team member')
      }
    } catch (error) {
      logService.error(`Update Team Member Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const deleteTeamMember = async (id) => {
    try {
      loading.value = true
      logService.info(`Deleting team member: ${id}`)

      const response = await apiService.delete(`/team-members/${id}`)

      if (response.success) {
        teamMembers.value = teamMembers.value.filter(m => m.id !== id)

        uiStore.showNotification({
          type: 'success',
          title: 'Team Member Deleted',
          message: 'Team member has been deleted successfully.'
        })

        return true
      } else {
        throw new Error(response.message || 'Failed to delete team member')
      }
    } catch (error) {
      logService.error(`Delete Team Member Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Deletion Failed',
        message: error.message
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const uploadImage = async (memberId, file) => {
    try {
      logService.info(`Uploading image for team member: ${memberId}`)
      const formData = new FormData()
      formData.append('image', file)

      const response = await apiService.upload(`/team-members/${memberId}/upload-image`, formData)

      if (response.success) {
        // Update the team member in the list with new image URL
        const index = teamMembers.value.findIndex(m => m.id === memberId)
        if (index !== -1) {
          teamMembers.value[index].image = response.data.image
          teamMembers.value[index].image_url = response.data.image_url
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Image Uploaded',
          message: 'Profile image has been uploaded successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to upload image')
      }
    } catch (error) {
      logService.error(`Upload Image Error [${memberId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Upload Failed',
        message: error.message
      })
      return null
    }
  }

  return {
    // State
    teamMembers,
    loading,

    // API Actions
    fetchTeamMembers,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    uploadImage
  }
})
